import { useState, FormEvent } from 'react';
import { Auth } from 'aws-amplify';
import { toast } from 'react-toastify';
import { useTranslation } from 'next-i18next';

import { getAuthErrorMessage, Mixpanel } from '../../shared/utils';

type AccountVerifyProps = {
  email: string;
};

const AccountVerify = ({ email }: AccountVerifyProps) => {
  const [code, setCode] = useState<string>('');
  const [showEmailField, setShowEmailField] = useState<boolean>(false);
  const [newEmail, setNewEmail] = useState<string>('');
  const { t } = useTranslation('components');

  const confirmSignUp = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    Mixpanel.track('Clicked verify account', email);

    Auth.confirmSignUp(email, code)
      .then(() => {
        Mixpanel.track('Succesfully verified account', email);
        toast.success(t('ACCOUNT_VERIFY.CONFIRM_SUCCESS_MESSAGE'));
      })
      .catch((error) => {
        const stringError = String(error);

        if (stringError.includes('ExpiredCodeException')) {
          Mixpanel.track('Attempted to verify account with expired code', email);

          return toast.error(t('ACCOUNT_VERIFY.CODE_EXPIRED_ERROR_MESSAGE'));
        }

        if (stringError.includes('CodeMismatchException')) {
          Mixpanel.track('Attempted to verify account with wrong code', email);

          return toast.error(t('ACCOUNT_VERIFY.CODE_MISMATCH_ERROR_MESSAGE'));
        }

        const errorMessage = getAuthErrorMessage(error);

        Mixpanel.track('Something went wrong whilst verifying account', email, { message: errorMessage });

        toast.error(errorMessage);
      });
  };

  const resendCode = async () => {
    Mixpanel.track('Clicked request verification code', email);

    if (!email) {
      return setShowEmailField(true);
    }

    try {
      await Auth.resendSignUp(email);

      Mixpanel.track('Succesfully requested new code', email);

      toast.success(t('ACCOUNT_VERIFY.NEW_CODE_SUCCESS_MESSAGE'));
    } catch (error: unknown) {
      Mixpanel.track('Something went wrong whilst requesting a new code', email);

      toast.error(t('ACCOUNT_VERIFY.NEW_CODE_ERROR_MESSAGE'));
    }
  };

  return (
    <form onSubmit={confirmSignUp} method="post">
      <div>
        {showEmailField && (
          <label>
            {t('ACCOUNT_VERIFY.EMAIL_LABEL')}
            <input
              name="code"
              placeholder={t('ACCOUNT_VERIFY.EMAIL_PLACEHOLDER')}
              required
              value={newEmail}
              onChange={(e) => setNewEmail(e.currentTarget.value)}
              autoComplete="email"
            />
          </label>
        )}
        <label>
          {t('ACCOUNT_VERIFY.CODE_LABEL')}
          <input
            inputMode="numeric"
            name="code"
            maxLength={6}
            required
            value={code}
            onChange={(e) => setCode(e.currentTarget.value)}
            autoComplete="off"
          />
        </label>
        <a onClick={resendCode}>{t('ACCOUNT_VERIFY.RESEND_CODE_CTA')}</a>
      </div>

      <button className="primary mt-16 w-full">{t('ACCOUNT_VERIFY.SUBMIT_CODE_CTA')}</button>
    </form>
  );
};

export default AccountVerify;
